import { apiGet, apiPatch, apiPost } from "./client";
import {
  deviceInstallHeaders,
  getOrCreateDeviceInstallId,
} from "@/modules/pos/sync/device-install-id";

export type PosTerminal = {
  id: string;
  code: string;
  name: string;
  branchId: string;
  warehouseId: string;
  priceListId: string;
  branchSlug: string;
  branchName: string;
  deactivatedAt: string | null;
  deviceBound?: boolean;
};

export type PosPayMethod = {
  id: string;
  code: string;
  name: string;
  isDefault: boolean;
  worksOffline: boolean;
};

export type PosCatalogItem = {
  id: string;
  name: string;
  sku: string | null;
  barcode: string | null;
  kind: string;
  trackLot?: boolean;
  trackSerial?: boolean;
  price: string;
  onHand: string;
  updatedAt: string;
  deleted?: boolean;
};

export type PosSession = {
  terminal: {
    id: string;
    code: string;
    name: string;
    warehouseId: string;
    priceListId: string;
    branchId: string;
    branchSlug: string | null;
    branchName: string | null;
    deviceBound?: boolean;
  };
  drawer: {
    id: string;
    status: string;
    openingFloat: string;
    /** Expected cash at close (float + cash sales − refunds) */
    expectedCash?: string;
    openedAt: string;
  } | null;
  payMethods: PosPayMethod[];
  numberRange: {
    nextSeq: number;
    rangeEnd: number;
    prefix: string;
    /** Present on fresh session; older offline cache may omit → default 4 */
    padWidth?: number;
  } | null;
  company: {
    /** Present on fresh session; older offline cache may omit */
    name?: string;
    taxPriceMode: "inclusive" | "exclusive";
    discountSoftCapPct: number;
    /** Present on fresh session; omit → treat as true (legacy cache) */
    allowOversell?: boolean;
    currencyCode: string;
    taxRatePct: number;
  };
};

export function listPosTerminals(
  companyRef: string,
  opts?: { includeDeactivated?: boolean },
) {
  const q = opts?.includeDeactivated ? "?all=1" : "";
  return apiGet<{ terminals: PosTerminal[]; showBranchPicker?: boolean }>(
    `/c/${companyRef}/pos/terminals${q}`,
  );
}

export function createPosTerminal(
  companyRef: string,
  body: { code: string; name: string; warehouseId?: string },
) {
  return apiPost<{ terminal: { id: string; code: string; name: string } }>(
    `/c/${companyRef}/pos/terminals`,
    body,
  );
}

export function updatePosTerminal(
  companyRef: string,
  terminalId: string,
  body: { name: string },
) {
  return apiPatch<{ terminal: { id: string; code: string; name: string } }>(
    `/c/${companyRef}/pos/terminals/${terminalId}`,
    body,
  );
}

export function deactivatePosTerminal(
  companyRef: string,
  terminalId: string,
) {
  return apiPost<{ terminal: { id: string; deactivated: boolean } }>(
    `/c/${companyRef}/pos/terminals/${terminalId}/deactivate`,
  );
}

export function reactivatePosTerminal(
  companyRef: string,
  terminalId: string,
) {
  return apiPost<{ terminal: { id: string; deactivated: boolean } }>(
    `/c/${companyRef}/pos/terminals/${terminalId}/reactivate`,
  );
}

export function getPosSession(companyRef: string, terminalId: string) {
  return apiGet<PosSession>(
    `/c/${companyRef}/pos/terminals/${terminalId}/session`,
    { headers: deviceInstallHeaders() },
  );
}

/** Bind this browser to the terminal (Open POS) — plan/28 */
export function bindPosTerminal(
  companyRef: string,
  terminalId: string,
  opts?: { force?: boolean },
) {
  return apiPost<{
    terminal: { id: string; deviceBound: boolean; rebound: boolean };
  }>(
    `/c/${companyRef}/pos/terminals/${terminalId}/bind`,
    {
      deviceInstallId: getOrCreateDeviceInstallId(),
      ...(opts?.force ? { force: true } : {}),
    },
    { headers: deviceInstallHeaders() },
  );
}

export function unbindPosTerminal(companyRef: string, terminalId: string) {
  return apiPost<{ terminal: { id: string; deviceBound: boolean } }>(
    `/c/${companyRef}/pos/terminals/${terminalId}/unbind`,
  );
}

export function openPosDrawer(
  companyRef: string,
  terminalId: string,
  body?: { id?: string; openingFloat?: string; clientCreatedAt?: string },
) {
  return apiPost<{
    drawer: {
      id: string;
      status: string;
      openingFloat: string;
      openedAt: string;
    };
    alreadyOpen: boolean;
  }>(
    `/c/${companyRef}/pos/terminals/${terminalId}/drawer/open`,
    {
      ...body,
      deviceInstallId: getOrCreateDeviceInstallId(),
    },
    { headers: deviceInstallHeaders() },
  );
}

export function closePosDrawer(
  companyRef: string,
  terminalId: string,
  body: {
    closingCashCounted: string;
    confirmVariance?: boolean;
  },
) {
  return apiPost<{
    drawer: {
      id: string;
      status: string;
      expectedCash?: string;
      closingCashCounted?: string;
      cashVariance?: string;
    };
  }>(
    `/c/${companyRef}/pos/terminals/${terminalId}/drawer/close`,
    {
      ...body,
      deviceInstallId: getOrCreateDeviceInstallId(),
    },
    { headers: deviceInstallHeaders() },
  );
}

export function fetchPosCatalog(
  companyRef: string,
  terminalId: string,
  opts?: { since?: string; sinceId?: string },
) {
  const q = new URLSearchParams();
  if (opts?.since) q.set("since", opts.since);
  if (opts?.sinceId) q.set("sinceId", opts.sinceId);
  const qs = q.toString();
  return apiGet<{
    channel?: string;
    data: PosCatalogItem[];
    lastUpdated: string | null;
    lastId?: string | null;
    terminalId: string;
    priceListId: string;
    warehouseId: string;
  }>(
    `/c/${companyRef}/pos/terminals/${terminalId}/catalog${qs ? `?${qs}` : ""}`,
  );
}

export function voidPosTicket(
  companyRef: string,
  ticketId: string,
  body?: {
    reason?: string;
    idempotencyKey?: string;
    overrideRequestId?: string;
    offlinePinApprove?: {
      id?: string;
      permissionKey: string;
      approverUserId: string;
      summary?: string;
    };
    /** When voiding from the bound register — plan/28 */
    fromTerminal?: boolean;
  },
) {
  const { fromTerminal, ...rest } = body ?? {};
  return apiPost<{
    ticket: { id: string; number: string; lifecycle: string };
    glError: string | null;
    replayed: boolean;
  }>(
    `/c/${companyRef}/pos/tickets/${ticketId}/void`,
    fromTerminal
      ? {
          ...rest,
          deviceInstallId: getOrCreateDeviceInstallId(),
        }
      : rest,
    fromTerminal ? { headers: deviceInstallHeaders() } : undefined,
  );
}

export function listTerminalTickets(companyRef: string, terminalId: string) {
  return apiGet<{
    tickets: Array<{
      id: string;
      number: string;
      lifecycle: string;
      grossTotal: string;
      postedAt: string | null;
    }>;
  }>(`/c/${companyRef}/pos/terminals/${terminalId}/tickets`);
}

export function listRecentPosTickets(companyRef: string) {
  return apiGet<{
    tickets: Array<{
      id: string;
      number: string;
      grossTotal: string;
      postedAt: string | null;
    }>;
  }>(`/c/${companyRef}/pos/tickets`);
}

export type PosTicketListRow = {
  id: string;
  number: string;
  lifecycle: string;
  grossTotal: string;
  terminalId: string;
  terminalCode: string;
  terminalName: string;
  syncStatus: string;
  postedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type PosTicketListPage = {
  tickets: PosTicketListRow[];
  hasMore?: boolean;
  nextCursor?: string | null;
  total?: number;
};

export type PosTicketListSort =
  | "number"
  | "lifecycle"
  | "grossTotal"
  | "terminalName"
  | "createdAt";

export function listPosTickets(
  companyRef: string,
  opts?: {
    q?: string;
    limit?: number;
    cursor?: string | null;
    lifecycle?: string;
    terminalId?: string;
    sort?: PosTicketListSort;
    sortDir?: "asc" | "desc";
    signal?: AbortSignal;
  },
) {
  const params = new URLSearchParams();
  if (opts?.q?.trim()) params.set("q", opts.q.trim());
  if (opts?.limit != null) params.set("limit", String(opts.limit));
  if (opts?.cursor) params.set("cursor", opts.cursor);
  if (
    opts?.lifecycle === "draft" ||
    opts?.lifecycle === "parked" ||
    opts?.lifecycle === "posted" ||
    opts?.lifecycle === "void"
  ) {
    params.set("lifecycle", opts.lifecycle);
  }
  if (opts?.terminalId) params.set("terminalId", opts.terminalId);
  if (opts?.sort && opts.sort !== "number") params.set("sort", opts.sort);
  if (opts?.sortDir === "desc") params.set("sortDir", "desc");
  const qs = params.toString();
  return apiGet<PosTicketListPage>(
    `/c/${companyRef}/pos/tickets${qs ? `?${qs}` : ""}`,
    { signal: opts?.signal },
  );
}

export type PosTicketDetail = {
  ticket: {
    id: string;
    number: string;
    lifecycle: string;
    terminalId: string;
    terminalCode: string;
    terminalName: string;
    branchId: string;
    branchSlug: string;
    branchName: string;
    warehouseId: string;
    taxPriceMode: "inclusive" | "exclusive";
    taxRatePct: string;
    discountPct: string;
    discountAmount: string;
    netTotal: string;
    taxTotal: string;
    grossTotal: string;
    notes: string | null;
    cashierUserId: string | null;
    cashierLabel: string | null;
    syncStatus: string;
    postedAt: string | null;
    voidedAt: string | null;
    createdAt: string;
  };
    lines: Array<{
      id: string;
      productId: string;
      description: string | null;
      qty: string;
      unitPrice: string;
      discountPct: string;
      lineNet: string;
      lineTax: string;
      lineGross: string;
      lotCode: string | null;
      serialCode: string | null;
      productName: string;
    }>;
  tenders: Array<{
    id: string;
    amount: string;
    changeGiven: string;
    payMethodId: string;
    payMethodName: string;
    payMethodCode: string;
  }>;
  company: { name: string; currencyCode: string };
};

export function getPosTicket(companyRef: string, ticketId: string) {
  return apiGet<PosTicketDetail>(
    `/c/${companyRef}/pos/tickets/${ticketId}`,
  );
}

export function sellPosTicket(
  companyRef: string,
  terminalId: string,
  body: {
    id: string;
    number?: string;
    idempotencyKey: string;
    drawerId?: string;
    cashierUserId?: string;
    discountPct?: string;
    taxRatePct?: string;
    taxPriceMode?: "inclusive" | "exclusive";
    clientCreatedAt?: string;
    lines: Array<{
      id: string;
      productId: string;
      qty: string;
      unitPrice?: string;
      lotCode?: string;
      serialCode?: string;
    }>;
    tender: {
      id: string;
      payMethodId: string;
      amount: string;
    };
    confirmOversell?: boolean;
    confirmDiscountOverCap?: boolean;
    overrideRequestId?: string;
    offlinePinApprove?: unknown;
    notes?: string;
  },
) {
  return apiPost<{
    ticket: {
      id: string;
      number: string;
      grossTotal: string;
      netTotal: string;
      taxTotal: string;
      changeGiven?: string;
      lifecycle: string;
    };
    oversellProductIds?: string[];
    oversell?: boolean;
    discountOverCap?: boolean;
    glError?: string | null;
    replayed: boolean;
  }>(
    `/c/${companyRef}/pos/terminals/${terminalId}/sell`,
    {
      ...body,
      deviceInstallId: getOrCreateDeviceInstallId(),
    },
    { headers: deviceInstallHeaders() },
  );
}

export type ParkedTicketSummary = {
  id: string;
  number: string;
  grossTotal: string;
  terminalId: string;
  terminalName: string;
  itemCount: number;
  createdAt?: string;
};

export function parkPosTicket(
  companyRef: string,
  terminalId: string,
  body: {
    id: string;
    idempotencyKey: string;
    drawerId?: string;
    number?: string;
    discountPct?: string;
    taxRatePct?: string;
    taxPriceMode?: "inclusive" | "exclusive";
    notes?: string;
    clientCreatedAt?: string;
    cashierUserId?: string;
    lines: Array<{
      id: string;
      productId: string;
      qty: string;
      unitPrice: string;
      lotCode?: string;
      serialCode?: string;
    }>;
  },
) {
  return apiPost<{
    ticket: ParkedTicketSummary & { lifecycle: string };
    replayed: boolean;
  }>(
    `/c/${companyRef}/pos/terminals/${terminalId}/park`,
    {
      ...body,
      deviceInstallId: getOrCreateDeviceInstallId(),
    },
    { headers: deviceInstallHeaders() },
  );
}

export function listParkedPosTickets(companyRef: string) {
  return apiGet<{ tickets: ParkedTicketSummary[] }>(
    `/c/${companyRef}/pos/parked`,
  );
}

export function recallParkedPosTicket(companyRef: string, ticketId: string) {
  return apiPost<{
    ticket: {
      id: string;
      number: string;
      terminalId: string;
      terminalName: string;
      taxPriceMode: "inclusive" | "exclusive";
      taxRatePct: string;
      discountPct: string;
      grossTotal: string;
      itemCount: number;
      notes: string | null;
    };
    lines: Array<{
      productId: string;
      name: string;
      unitPrice: string;
      qty: number;
      trackLot: boolean;
      trackSerial: boolean;
      lotCode: string | null;
      serialCode: string | null;
    }>;
  }>(`/c/${companyRef}/pos/tickets/${ticketId}/recall`);
}
